<script setup lang="ts">
import { computed, ref } from 'vue'
import type { IMessage } from '@/modules/chat-matrix-fast/types/IChat'

const props = defineProps<{
  messages: IMessage[]
  canUnpin?: boolean
  busy?: boolean
}>()

const emit = defineEmits<{
  (e: 'select', message: IMessage): void
  (e: 'unpin', message: IMessage): void
}>()

const expanded = ref(false)
const cycleIndex = ref(0)

const count = computed(() => props.messages.length)

const activeMessage = computed(() => {
  if (!count.value) {
    return null
  }

  const index = ((cycleIndex.value % count.value) + count.value) % count.value
  return props.messages[index] ?? null
})

const previewText = computed(() => {
  const message = activeMessage.value
  if (!message) {
    return ''
  }

  const text = (message.content || '').trim()
  if (text) {
    return text
  }

  if (message.attachments?.length) {
    return 'Вложение'
  }

  return 'Сообщение'
})

const authorLabel = computed(() => {
  const message = activeMessage.value
  if (!message) {
    return ''
  }

  return message.created_by?.full_name
    || message.author_name
    || message.author
    || ''
})

const handleSelect = () => {
  const message = activeMessage.value
  if (!message) {
    return
  }

  if (count.value > 1) {
    cycleIndex.value = (cycleIndex.value + 1) % count.value
  }

  emit('select', message)
}

const handleUnpin = (message: IMessage, event: MouseEvent) => {
  event.stopPropagation()
  emit('unpin', message)
}
</script>

<template>
  <div
    v-if="count > 0"
    class="pinned-messages-bar"
  >
    <button
      type="button"
      class="pinned-messages-bar__main"
      @click="handleSelect"
    >
      <VIcon
        icon="ri-pushpin-2-fill"
        size="18"
        class="pinned-messages-bar__icon"
      />
      <div class="pinned-messages-bar__text">
        <div class="pinned-messages-bar__meta">
          <span>Закреплённые</span>
          <span v-if="count > 1">{{ (cycleIndex % count) + 1 }}/{{ count }}</span>
        </div>
        <div class="pinned-messages-bar__preview text-truncate">
          <span
            v-if="authorLabel"
            class="pinned-messages-bar__author"
          >{{ authorLabel }}:</span>
          {{ previewText }}
        </div>
      </div>
    </button>

    <button
      v-if="count > 1"
      type="button"
      class="pinned-messages-bar__toggle"
      :aria-expanded="expanded"
      :aria-label="expanded ? 'Скрыть список' : 'Показать список'"
      @click="expanded = !expanded"
    >
      <VIcon :icon="expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'" size="18" />
    </button>

    <div
      v-if="expanded && count > 1"
      class="pinned-messages-bar__list"
    >
      <button
        v-for="message in messages"
        :key="message.matrix_event_id || message.id"
        type="button"
        class="pinned-messages-bar__item"
        @click="emit('select', message)"
      >
        <div class="pinned-messages-bar__item-text text-truncate">
          <span class="pinned-messages-bar__author">
            {{ message.created_by?.full_name || message.author_name || message.author }}:
          </span>
          {{ (message.content || '').trim() || (message.attachments?.length ? 'Вложение' : 'Сообщение') }}
        </div>
        <button
          v-if="canUnpin"
          type="button"
          class="pinned-messages-bar__unpin"
          :disabled="busy"
          aria-label="Открепить"
          @click="handleUnpin(message, $event)"
        >
          <VIcon icon="ri-unpin-line" size="16" />
        </button>
      </button>
    </div>

    <button
      v-else-if="canUnpin && activeMessage"
      type="button"
      class="pinned-messages-bar__unpin"
      :disabled="busy"
      aria-label="Открепить"
      @click="handleUnpin(activeMessage, $event)"
    >
      <VIcon icon="ri-unpin-line" size="16" />
    </button>
  </div>
</template>

<style scoped lang="scss">
.pinned-messages-bar {
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgb(var(--v-theme-surface));
}

.pinned-messages-bar__main {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  color: inherit;
  padding: 0;
}

.pinned-messages-bar__icon {
  color: rgb(var(--v-theme-primary));
  flex-shrink: 0;
}

.pinned-messages-bar__text {
  min-width: 0;
  flex: 1;
}

.pinned-messages-bar__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
  line-height: 1.2;
}

.pinned-messages-bar__preview {
  font-size: 0.8125rem;
  color: rgba(var(--v-theme-on-surface), 0.7);
  line-height: 1.3;
  margin-top: 2px;
}

.pinned-messages-bar__author {
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), 0.85);
  margin-right: 4px;
}

.pinned-messages-bar__toggle,
.pinned-messages-bar__unpin {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: rgba(var(--v-theme-on-surface), 0.6);
  cursor: pointer;
  flex-shrink: 0;
}

.pinned-messages-bar__toggle:hover,
.pinned-messages-bar__unpin:hover {
  background: rgba(var(--v-theme-on-surface), 0.06);
  color: rgb(var(--v-theme-on-surface));
}

.pinned-messages-bar__unpin:disabled {
  opacity: 0.5;
  cursor: default;
}

.pinned-messages-bar__list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 5;
  max-height: 220px;
  overflow: auto;
  background: rgb(var(--v-theme-surface));
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
}

.pinned-messages-bar__item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: transparent;
  cursor: pointer;
  text-align: left;
  color: inherit;
}

.pinned-messages-bar__item:hover {
  background: rgba(var(--v-theme-on-surface), 0.04);
}

.pinned-messages-bar__item-text {
  min-width: 0;
  flex: 1;
  font-size: 0.8125rem;
}
</style>
